// =============================================================================
// Accountant Dashboard Page (Tax Portal)
// =============================================================================
// Dashboard showing deals accountant has access to for K-1 preparation
// Flow: Dashboard -> Deal -> Tax Year -> Investor -> K-1 Upload
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../components/ui';
import { accountantService, type AccountantDeal } from '../../services/accountant.service';
import { useAuth } from '../../hooks/useAuth';

// =============================================================================
// Component
// =============================================================================

export function AccountantDashboardPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState<AccountantDeal[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountantService.getDeals();
      setDeals(data);
    } catch (err) {
      console.error('Failed to load deals:', err);
      setError('Failed to load deals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      MULTIFAMILY: 'Multifamily',
      RETAIL: 'Retail',
      OFFICE: 'Office',
      INDUSTRIAL: 'Industrial',
      MIXED_USE: 'Mixed Use',
      HOTEL: 'Hotel',
      SELF_STORAGE: 'Self Storage',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">{error}</h3>
        <button
          onClick={loadDeals}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';
  const currentTaxYear = new Date().getFullYear() - 1;

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          Welcome, {firstName}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Tax Portal - Select a deal to manage K-1 documents
        </p>
      </div>

      {/* Deals List */}
      {deals.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Deals Available</h3>
            <p className="mt-2 text-sm text-gray-500">
              You don't have access to any deals yet. Please contact an administrator.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Your Deals</h2>
          {deals.map((deal) => (
            <Link key={deal.id} to={`/accountant/deals/${deal.id}`}>
              <Card padding="md" hover className="transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{deal.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                          {getPropertyTypeLabel(deal.propertyType)}
                        </span>
                        <span className="text-sm text-gray-500">{deal.address}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <UsersIcon className="h-4 w-4" />
                          {deal.investorCount} investors
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <DocumentTextIcon className="h-4 w-4" />
                          {deal.k1Count} K-1s uploaded
                        </span>
                        {deal.taxYears.length > 0 && (
                          <span className="text-gray-500">
                            Tax years: {deal.taxYears.slice(0, 3).join(', ')}
                            {deal.taxYears.length > 3 && '...'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {deals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card padding="md">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600">
                {deals.reduce((sum, d) => sum + d.investorCount, 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Investors</p>
            </div>
          </Card>
          <Card padding="md">
            <div className="text-center">
              <p className="text-3xl font-bold text-success-600">
                {deals.reduce((sum, d) => sum + d.k1Count, 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">K-1s Uploaded</p>
            </div>
          </Card>
          <Card padding="md">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{currentTaxYear}</p>
              <p className="text-sm text-gray-500 mt-1">Current Tax Year</p>
            </div>
          </Card>
        </div>
      )}

      {/* Access Information */}
      <Card padding="md" className="bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <ClockIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-amber-900">Temporary Access</h3>
            <p className="text-sm text-amber-700 mt-1">
              You have been granted temporary access to upload K-1 documents for the deals listed above.
              Select a deal to view investors and manage their K-1 documents.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AccountantDashboardPage;
